import { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import {
  MediaPlayerProvider,
  useMediaPlayerStore
} from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { PLAY_BUTTON } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { PlayButton } from '~/ui/buttons';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import ReactDOM from 'react-dom';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';

const selector = (s: MediaPlayerStore) =>
  select(s, 'loadMedia', 'status', 'toggle', 'progress');

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface Props {
  id?: string;
  mediaUrl: string;
  subtitlesUrl?: string;
  blocking?: boolean;
}

const Button = ({ id, mediaUrl, subtitlesUrl, blocking = false }: Props) => {
  const [showAudioPopup, setShowAudioPopup] = useState(false);

  const { loadMedia, status, toggle, progress } = useMediaPlayerStore(
    selector,
    shallow
  );

  const { contentEditable, updateFile } = useEditorStore();

  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
  }, []);

  const { addBlocker, removeBlocker } = useSequenceStore(seqSelector, shallow);

  useEffect(() => {
    if (!blocking) return;
    addBlocker(id || '');

    if (status == 'nomedia') {
      removeBlocker(id || '');
    }
  }, [status]);

  useEffect(() => {
    if (!blocking) return;
    if ((mediaUrl && status === 'done') || !mediaUrl) {
      removeBlocker(id || '');
    }
  }, [status, mediaUrl]);

  const onPress = () => {
    if (contentEditable) {
      setShowAudioPopup(true);
    } else {
      toggle();
    }
  };

  const onSelectNarration = (audio: EditorMediaFile) => {
    if (id && audio?.id) {
      updateFile(id.toString(), audio, 'audios');
    }

    setShowAudioPopup(false);
  };

  return (
    <>
      <x.div
        w="85px"
        h="85px"
        borderRadius="50%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
      >
        <AudioPlayer />
        <PlayButton
          onClick={onPress}
          state={status === 'playing' ? 'playing' : 'speaker'}
          progress={progress}
        />
      </x.div>
      {showAudioPopup &&
        contentEditable &&
        ReactDOM.createPortal(
          <NarrationPopup
            onClose={() => setShowAudioPopup(false)}
            skipUpdate={true}
            onSelectCallback={onSelectNarration}
            id={id}
            skipSequenceNarration={true}
          />,
          document.body
        )}
    </>
  );
};

export type PlayButtonDefinition = ItemDefinition<typeof PLAY_BUTTON, Props>;

export class PlayBtn extends Item<typeof PLAY_BUTTON, Props> {
  protected render({
    id,
    mediaUrl,
    subtitlesUrl,
    blocking = false
  }: RenderProps<Props>) {
    return (
      <MediaPlayerProvider>
        <Button
          id={id}
          mediaUrl={mediaUrl}
          subtitlesUrl={subtitlesUrl}
          blocking={blocking}
        />
        {ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      </MediaPlayerProvider>
    );
  }
}

registry[PLAY_BUTTON] = PlayBtn;
